import { Spacing } from '@persona-id/design-tokens';
import { head, intersection } from 'lodash';
import { sortBy } from 'lodash/fp';
import qs from 'qs';
import { MouseEvent, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Prompt, useLocation } from 'react-router';
import { Link } from 'react-router-dom-v5-compat';
import { Field, formValueSelector, InjectedFormProps, reduxForm } from 'redux-form';
import styled from 'styled-components';

import CopyableField from 'dashboard/components/CopyableField';
import InputField from 'dashboard/components/FormControls/InputField';
import MultiSelectField from 'dashboard/components/FormControls/MultiSelectField';
import ReactSelectField, {
  ReactCreatableSelectField,
} from 'dashboard/components/FormControls/ReactSelectField';
import ToggleSliderField from 'dashboard/components/FormControls/ToggleSliderField';
import { MultiValuePlaceholder } from 'dashboard/components/ReactSelect';
import { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import Section from 'dashboard/layouts/FormLayout/Section';
import { FORM_INQUIRY_TEMPLATE_CONFIG } from 'dashboard/lib/constants/formNames';
import { Template, VerifyTicketRouting } from 'dashboard/models/template';
import { Theme } from 'dashboard/models/theme';
import { User } from 'dashboard/models/user';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import {
  COUNTRIES,
  CountryCode,
  INQUIRY_LANGUAGES_ENGLISH,
  InquiryIso6391LanguageCode,
} from 'lib/constants/locales';
import { isRequired, withMessage } from 'lib/utils/formValidation';

export interface LegacyTemplateConfigFormData {
  name: string;
  'name-display': string;
  'enabled-locales': InquiryIso6391LanguageCode[];
  'enabled-country-codes': CountryCode[];
  'verify-ticket-routing': VerifyTicketRouting;
  'support-routed-emails': string[];
  // This can be nil when creating a new template and the org does not have any
  // themes yet
  // https://github.com/persona-id/persona-web/blob/f6bbfe145883a380ab965b091801f628983fbf43/app/models/template.rb#L357
  'default-theme-id': string | undefined;
}

const SectionDescription = styled('div')`
  margin-bottom: ${Spacing[4]};
`;

const CountryDisplayWrapper = styled('div')`
  display: flex;
  flex-direction: column;
`;

const selector = formValueSelector(FORM_INQUIRY_TEMPLATE_CONFIG);

const validate = {
  name: [withMessage('Please enter a template name', isRequired)],
  'enabled-locales': [withMessage('Please select at least one language', isRequired)],
};

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const countryCodeOptions = Object.keys(COUNTRIES).map((options: CountryCode) => ({
  value: options,
  label: COUNTRIES[options],
}));

const sortedCountryCodeOptions = sortBy('label')(countryCodeOptions);

const LANGUAGE_OPTIONS = Object.keys(INQUIRY_LANGUAGES_ENGLISH).map(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  (languageCode: InquiryIso6391LanguageCode) => ({
    value: languageCode,
    label: INQUIRY_LANGUAGES_ENGLISH[languageCode],
  }),
);

interface FormLegacyTemplateProps {
  template: Template;
  themes: Theme[];
  users: User[];
  canEdit: boolean;
  isSendingTestSupportEmail: boolean;
  isFormPristine: boolean;
  handleTestSupportFormEmail: (template: Template) => void;
}

const FormLegacyTemplate = ({
  template,
  themes,
  users,
  canEdit,
  isSendingTestSupportEmail,
  isFormPristine,
  handleTestSupportFormEmail,
  handleSubmit,
  submitting,
  pristine,
  reset,
}: FormLegacyTemplateProps & InjectedFormProps) => {
  const location = useLocation();

  // Force saving when changing tabs
  useEffect(() => {
    reset();
  }, [location.pathname]);

  const selectedThemeId = useSelector<RootState, string | undefined>((state) =>
    selector(state, 'default-theme-id'),
  );

  const supportRoutedEmailsFormState = useSelector<RootState, string[]>(
    (state) => selector(state, 'support-routed-emails') ?? [],
  );
  const verifyRoutingEnabledFormState = useSelector<RootState, VerifyTicketRouting | undefined>(
    (state) => selector(state, 'verify-ticket-routing'),
  );

  const countryCount = useSelector<RootState, number>(
    (state) => selector(state, 'enabled-country-codes')?.length ?? 0,
  );

  // set label to max if actual count is 0, as default is 'All Selected'
  const countryCountLabel = countryCount === 0 ? countryCodeOptions.length : countryCount;
  const allCountriesSelected = countryCountLabel === countryCodeOptions.length;

  const templateFilterLabelTextMapper = useLabelTextMapper(countryCodeOptions, 'Countries', {
    emptyIsAll: false,
  });

  const handleTestSupportEmailClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      handleTestSupportFormEmail(template);
    },
    [template, handleTestSupportFormEmail],
  );

  const disabled = submitting || !canEdit;

  const userEmails = users.map((user) => user.attributes['email-address']).filter(Boolean);

  const userEmailOptions = intersection([...supportRoutedEmailsFormState, ...userEmails]).map(
    (email) => {
      return { label: email, value: email };
    },
  );

  const formVerifyRoutingEnabled = verifyRoutingEnabledFormState === VerifyTicketRouting.Enabled;

  return (
    <>
      <Prompt
        when={!pristine && !submitting}
        message='You have unsaved changes. Are you sure you want to leave?'
      />
      <form onSubmit={handleSubmit}>
        <Card>
          <Card.Body>
            <Card.Title>Name</Card.Title>
            <Section
              cols={2}
              colsMobile={1}
              data-test='form-template-config__general-tab--basic-information'
            >
              <Field
                component={InputField}
                name='name'
                id='name'
                type='text'
                data-test='form-template-config__general-tab--template-name'
                disabled={disabled}
                validate={validate.name}
                placeholder='Template name'
                label='Template name'
              />
              <Field
                component={InputField}
                name='name-display'
                id='name-display'
                type='text'
                data-test='form-template-config__general-tab--template-name-display'
                disabled={disabled}
                placeholder='Display name'
                label='Display name (Optional)'
              />
            </Section>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <Card.Title>Default Theme</Card.Title>
            <SectionDescription>
              <p>
                Edit themes and copy for each template{' '}
                <Link
                  to={{
                    pathname: '/dashboard/editor',
                    search: `${qs.stringify({
                      'selected-template-id': template.id,
                      'selected-theme-id': selectedThemeId || head(themes)?.id,
                    })}`,
                  }}
                >
                  here
                </Link>
                .
              </p>
            </SectionDescription>

            {themes.length > 1 && (
              <Section cols={2} colsMobile={1}>
                <Field
                  component={ReactSelectField}
                  label='Default theme'
                  name='default-theme-id'
                  id='default-theme-id'
                  isDisabled={disabled}
                  options={themes.map((t) => ({
                    label: t.attributes.name,
                    value: t.id,
                  }))}
                />
                <CopyableField
                  label='Theme ID'
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  value={selectedThemeId}
                  data-test='form-template-config__general-tab--theme-id'
                />
              </Section>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <Card.Title>Localized Languages</Card.Title>
            <SectionDescription>
              <p>Internationalize your inquiry flow by adding a language selector to the footer.</p>
              <Link to='/dashboard/contact-us'>Missing a language? Contact us</Link>
            </SectionDescription>
            <Section cols={2} colsMobile={1}>
              <Field
                component={ReactSelectField}
                name='enabled-locales'
                id='enabled-locales'
                options={LANGUAGE_OPTIONS}
                isMulti={true}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                placeholder={<MultiValuePlaceholder>All languages</MultiValuePlaceholder>}
                isDisabled={disabled}
              />
            </Section>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <Card.Title>Enabled Countries</Card.Title>
            <SectionDescription>
              <p>Add locations you would like to accept IDs from.</p>
              <Link to='/dashboard/contact-us'>
                Need a list of countries and supported documents? Contact us
              </Link>
            </SectionDescription>
            <Section cols={2} colsMobile={1}>
              <CountryDisplayWrapper>
                <Label size='md' weight='bold'>
                  Country options
                </Label>
                <Field
                  id='enabled-country-codes'
                  name='enabled-country-codes'
                  component={MultiSelectField}
                  selectOptionValues={sortedCountryCodeOptions}
                  labelTextMapper={templateFilterLabelTextMapper}
                  disabled={disabled}
                  selectAllEnabled
                  isSearchable
                />
              </CountryDisplayWrapper>
            </Section>
            <Section cols={1} colsMobile={1}>
              <CountryDisplayWrapper>
                <Label size='md' weight='bold'>{`Selected countries (${countryCountLabel})`}</Label>
                <Field
                  component={ReactSelectField}
                  name='enabled-country-codes'
                  id='enabled-country-codes'
                  // display all countries placeholder for both empty (all by default for templates) and all selected
                  options={allCountriesSelected ? [] : sortedCountryCodeOptions}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  placeholder={<MultiValuePlaceholder>All countries</MultiValuePlaceholder>}
                  components={{ DropdownIndicator: () => null }}
                  isDisabled={true}
                  isMulti={true}
                />
              </CountryDisplayWrapper>
            </Section>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <Card.Title>Support Emails</Card.Title>
            <SectionDescription>
              <p>Let your customers reach you via email during the inquiry flow.</p>
            </SectionDescription>
            <Section cols={2} colsMobile={1}>
              <Field
                component={ToggleSliderField}
                name='verify-ticket-routing'
                id='verify-ticket-routing'
                labelOn='On'
                labelOff='Off'
                parse={(value: boolean) =>
                  value ? VerifyTicketRouting.Enabled : VerifyTicketRouting.Disabled
                }
                format={(value: VerifyTicketRouting) => value === VerifyTicketRouting.Enabled}
              />
            </Section>
            {formVerifyRoutingEnabled && (
              <Section cols={2} colsMobile={1}>
                <Field
                  component={ReactCreatableSelectField}
                  name='support-routed-emails'
                  id='support-routed-emails'
                  options={userEmailOptions}
                  formatCreateLabel={(inputValue: string) => `Add new email: ${inputValue}`}
                  isMulti={true}
                  placeholder='No emails selected'
                  isDisabled={disabled}
                />
                <Spacer height={3} />
                <Button
                  type='submit'
                  width='button'
                  variant='basic'
                  id='form-inquiry-template__test-support-email-button'
                  data-test='form-inquiry-template__test-support-email-button'
                  isPending={isSendingTestSupportEmail}
                  disabled={!isFormPristine}
                  onClick={handleTestSupportEmailClick}
                >
                  Send a Test Email
                </Button>
              </Section>
            )}
          </Card.Body>
        </Card>
      </form>
    </>
  );
};

export default reduxForm<LegacyTemplateConfigFormData, FormLegacyTemplateProps>({
  form: FORM_INQUIRY_TEMPLATE_CONFIG,
  enableReinitialize: true,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(FormLegacyTemplate);
